/**
 * DataGridCellLongText — Expandable textarea cell variant
 *
 * Renders a textarea that grows to 3-4 lines.
 * Enter inserts a newline. Ctrl+Enter or blur commits.
 * Escape cancels.
 */

import { useRef, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { DataGridCellVariantProps } from './types';

export function DataGridCellLongText({
  value,
  onCommit,
  onCancel,
  onStartEditing,
  saveState,
}: DataGridCellVariantProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [localValue, setLocalValue] = useState(String(value ?? ''));

  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
      onStartEditing();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onCancel();
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      // Ctrl+Enter commits
      e.stopPropagation();
      e.preventDefault();
      onCommit(localValue);
    } else if (e.key === 'Tab') {
      // Let Tab bubble to grid keyboard handler
      onCommit(localValue);
    } else {
      // All other keys (including plain Enter for newline) stay in textarea
      e.stopPropagation();
    }
  };

  return (
    <div className="relative flex items-start w-full">
      <textarea
        ref={textareaRef}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={() => onCommit(localValue)}
        onKeyDown={handleKeyDown}
        rows={3}
        className="w-full bg-transparent text-sm outline-none border-none px-1 py-1 resize-none"
        aria-label="Edit long text"
      />
      {saveState.status === 'saving' && (
        <Loader2 className="absolute top-0.5 right-0.5 w-3 h-3 text-muted-foreground animate-spin" />
      )}
    </div>
  );
}
